/**
 * AdminFinancialKPIs - Cards de métricas financeiras (KPIs)
 * Exibe MRR Total, MRR Planos, MRR Addons, Tenants Pagantes, Ticket Médio,
 * Receita em Risco, MRR Real (Stripe) e Desconto Total
 */

import { DollarSign, CreditCard, Package, Users, TrendingUp, AlertTriangle, BadgePercent, BarChart3 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

/** Formata valores monetários em BRL */
const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

interface KPICardProps {
  title: string;
  value: string;
  icon: React.ElementType;
  variant?: 'default' | 'warning' | 'success';
  isLoading?: boolean;
}

/** Card individual de KPI */
function KPICard({ title, value, icon: Icon, variant = 'default', isLoading = false }: KPICardProps) {
  const bgClass = variant === 'warning'
    ? 'bg-destructive/10'
    : variant === 'success'
      ? 'bg-emerald-500/10'
      : 'bg-primary/10';

  const iconClass = variant === 'warning'
    ? 'text-destructive'
    : variant === 'success'
      ? 'text-emerald-600'
      : 'text-primary';

  return (
    <Card className={variant === 'warning' ? 'border-destructive/50' : ''}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">{title}</p>
            {isLoading ? (
              <Skeleton className="h-7 w-24" />
            ) : (
              <p className="text-xl font-bold text-foreground">{value}</p>
            )}
          </div>
          <div className={`p-2 rounded-lg ${bgClass}`}>
            <Icon className={`h-5 w-5 ${iconClass}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface AdminFinancialKPIsProps {
  mrrTotal: number;
  mrrPlans: number;
  mrrAddons: number;
  payingTenants: number;
  averageTicket: number;
  revenueAtRisk: number;
  /** MRR real cobrado no Stripe */
  mrrStripe: number | null;
  /** Desconto total (MRR tabela - MRR Stripe) */
  discountTotal: number | null;
  /** Indica se dados do Stripe estão carregando */
  isLoadingStripe?: boolean;
}

export function AdminFinancialKPIs({
  mrrTotal, mrrPlans, mrrAddons, payingTenants, averageTicket, revenueAtRisk,
  mrrStripe, discountTotal, isLoadingStripe = false,
}: AdminFinancialKPIsProps) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
      <KPICard title="MRR Total" value={formatBRL(mrrTotal)} icon={DollarSign} />
      <KPICard title="MRR Planos" value={formatBRL(mrrPlans)} icon={CreditCard} />
      <KPICard title="MRR Add-ons" value={formatBRL(mrrAddons)} icon={Package} />
      <KPICard title="Tenants Pagantes" value={String(payingTenants)} icon={Users} />
      <KPICard title="Ticket Médio" value={formatBRL(averageTicket)} icon={TrendingUp} />
      <KPICard
        title="Receita em Risco"
        value={formatBRL(revenueAtRisk)}
        icon={AlertTriangle}
        variant={revenueAtRisk > 0 ? 'warning' : 'default'}
      />
      {/* KPIs do Stripe */}
      <KPICard
        title="MRR Real (Stripe)"
        value={mrrStripe !== null ? formatBRL(mrrStripe) : '-'}
        icon={BarChart3}
        variant="success"
        isLoading={isLoadingStripe}
      />
      <KPICard
        title="Desconto Total"
        value={discountTotal !== null ? formatBRL(discountTotal) : '-'}
        icon={BadgePercent}
        variant={discountTotal && discountTotal > 0 ? 'warning' : 'default'}
        isLoading={isLoadingStripe}
      />
    </div>
  );
}
